import { useContext, useMemo } from 'react';
import type {
  PlanFeatureInput,
  PlanSuggestionInfoFragment,
} from '@frameio/graphql-client/generated';
import type { FeatureGateSuggestion } from '../contexts/FeatureGatesContext';
import {
  FeatureGatesLoadingContext,
  GetSuggestionContext,
} from '../contexts/FeatureGatesContext';

export type FeatureInput =
  | PlanFeatureInput
  | {
      featureList: Array<PlanFeatureInput>;
      operator: 'AND' | 'OR';
    };

type FeaturePlanSuggestion = {
  plan: NonNullable<PlanSuggestionInfoFragment['plan']>;
} & FeatureGateSuggestion;

const enterprisePlanSuggestion: FeaturePlanSuggestion['plan'] = {
  cost: 0,
  id: 'enterprise-plan',
  name: 'enterprise-plan',
  period: 'MONTHLY',
  tier: 'ENTERPRISE',
  title: 'Enterprise',
  userLimit: null,
  version: null,
};

function fallbackToEnterprisePlan(
  planSuggestion: FeatureGateSuggestion
): FeaturePlanSuggestion {
  return {
    ...planSuggestion,
    plan: planSuggestion.plan || enterprisePlanSuggestion,
  };
}

export function useFeatureGatesLoading(): boolean {
  return useContext(FeatureGatesLoadingContext);
}

/**
 * Reads a feature's plan suggestion. Returns undefined until loaded. For a
 * featureList, the AND/OR operator picks which feature's suggestion to surface.
 */
export function useFeatureGatePlanSuggestion(
  feature: FeatureInput
): FeaturePlanSuggestion | undefined {
  const getSuggestion = useContext(GetSuggestionContext);

  return useMemo((): FeaturePlanSuggestion | undefined => {
    if (typeof feature !== 'object') {
      const single = getSuggestion(feature);
      return single ? fallbackToEnterprisePlan(single) : undefined;
    }

    const suggestions = feature.featureList.map((name) => getSuggestion(name));
    const defaultSuggestion = suggestions[0];
    if (!defaultSuggestion) return undefined;

    // OR surfaces the first included feature; AND the first excluded one.
    const match = suggestions.find((suggestion) =>
      feature.operator === 'OR'
        ? suggestion?.includedInCurrentPlan
        : suggestion && !suggestion.includedInCurrentPlan
    );

    return fallbackToEnterprisePlan(match ?? defaultSuggestion);
  }, [feature, getSuggestion]);
}
